<script setup lang="ts">
import type { PropType } from 'vue';
import GaiaBreadcrumb from '../domain/navigation/GaiaBreadcrumb.vue';
import GaiaTopbar from '../domain/navigation/GaiaTopbar.vue';
import { gaiaBaseTextSize, gaiaNeutral, gaiaShellPadding } from '../themes/tokens';

defineProps({
  /** Width of the left rail. Mounts nothing on its own — the #rail slot does. */
  railWidth: {
    type: [String, Number] as PropType<string | number>,
    default: 280,
  },
  /**
   * Reading measure for the step body, centred in the space the rail leaves.
   * The wide default suits steps whose rows carry several columns of their own
   * — the playbook wizard's item cards, which lay Prompt, Outcomes and Response
   * type side by side. A wizard whose steps are plain forms should bind this
   * down per step rather than inherit the wide measure: Prepare signature draws
   * its form steps at 720px and only the field-placement step at 1160px.
   */
  contentMaxWidth: {
    type: String,
    default: '1160px',
  },
  /**
   * Padding around the content column. The top comes from `gaiaShellPadding`,
   * shared with the rail so both columns start at the same y; the sides are
   * `pageX`, the same edge the breadcrumb bar above uses.
   */
  contentPadding: {
    type: String,
    default: `${gaiaShellPadding.top} ${gaiaShellPadding.pageX}`,
  },
  /**
   * `absolute` fills the parent, so the rail and the step body each get a
   * scroll container and the footer stays pinned to the bottom. `static`
   * grows with its content and lets whatever mounts it do the scrolling —
   * the footer then flows after the body instead of pinning.
   */
  position: {
    type: String as PropType<'static' | 'absolute'>,
    default: 'absolute',
  },
  /**
   * Gives the step body the column's full height as a flex column, so a step
   * that manages its own scrolling — the drafting wizard's compose step, a
   * form beside a document — can put `flex: 1; min-height: 0` on its panes.
   * Off by default: an ordinary step flows and the body scrolls it. Same
   * switch as layout/Layout.vue's.
   */
  fill: {
    type: Boolean,
    default: false,
  },
});

// Page-level body size, as in layout/Layout.vue: components inside name a
// role from the type scale, anything that doesn't ask inherits this.
const baseTextSize = gaiaBaseTextSize;

const railBg = gaiaNeutral.surfaceSoft;
const railPadding = `${gaiaShellPadding.top} ${gaiaShellPadding.railX}`;
const stripBorder = gaiaNeutral.borderDivider;
const stripPadX = gaiaShellPadding.pageX;
</script>

<template>
  <!-- The wizard shell: the breadcrumb bar across the top (from #breadcrumb),
       then a step rail on the left, the active step's body in the middle, and
       a pinned action bar under it. Structural only — the rail's and the
       footer's chrome come entirely from the slotted content (GaiaStepRail,
       GaiaWizardFooter), the same split layout/Layout.vue uses for its drawer. -->
  <!-- `has-sider` only because the rail's n-layout-sider asks its nearest
       n-layout for it (naive warns otherwise); the frame is that layout's one
       child and fills it. -->
  <n-layout
    has-sider
    :position="position"
    class="gaia-wizard-layout"
    :style="{ fontSize: baseTextSize }"
  >
    <!-- The row is ours rather than naive's `has-sider`: the bar has to span
         rail and body, so it needs a column around the row. -->
    <div class="gaia-wizard-layout__frame">
      <gaia-topbar v-if="$slots.breadcrumb" class="gaia-wizard-layout__topbar">
        <gaia-breadcrumb>
          <slot name="breadcrumb" />
        </gaia-breadcrumb>
      </gaia-topbar>

      <div class="gaia-wizard-layout__row">
        <!-- The soft ground goes on the sider itself, not its `content-style`:
             `native-scrollbar="false"` puts the content in an auto-height scroll
             container, so a background there stops where the steps stop and leaves
             the rest of the rail white. -->
        <n-layout-sider
          v-if="$slots.rail"
          bordered
          :width="railWidth"
          :native-scrollbar="false"
          :style="{ background: railBg }"
          :content-style="{ padding: railPadding }"
        >
          <slot name="rail" />
        </n-layout-sider>

        <!-- naive has no primitive that stacks a scrolling body over a pinned
             action bar (n-layout-footer only pins in its own absolute mode, which
             needs the footer's height up front), so the column box is ours and
             only the body carries a scroller. -->
        <div class="gaia-wizard-layout__column">
          <!-- Fill mode takes the native scroll container: naive's custom
               scrollbar wraps the content in a box with no height of its own,
               so the step's `height: 100%` would resolve to nothing there. The
               body does not scroll in fill mode anyway — the step's panes do. -->
          <n-layout-content class="gaia-wizard-layout__body" :native-scrollbar="fill">
            <!-- The reading column: same capped, centred box as Layout.vue's. -->
            <div
              class="gaia-wizard-layout__content"
              :class="{ 'gaia-wizard-layout__content--fill': fill }"
              :style="{ maxWidth: contentMaxWidth, margin: '0 auto', padding: contentPadding }"
            >
              <slot />
            </div>
          </n-layout-content>
          <div v-if="$slots.footer" class="gaia-wizard-layout__footer">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </div>
  </n-layout>
</template>

<style scoped>
/* `flex: 1` because `has-sider` makes the layout a row: the frame, its only
   child, still takes the full width. */
.gaia-wizard-layout__frame {
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.gaia-wizard-layout__topbar {
  flex: none;
}

.gaia-wizard-layout__row {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  display: flex;
}

/* `min-height: 0` is load-bearing: as a flex item the column would otherwise
   refuse to shrink below its content, grow past the row, and push the footer
   off the screen the moment a step is taller than the viewport. */
.gaia-wizard-layout__column {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.gaia-wizard-layout__body {
  flex: 1 1 auto;
  min-height: 0;
}

.gaia-wizard-layout__footer {
  flex: none;
}

/* Fill mode: the step body is exactly the column, and whatever it mounts
   does the scrolling. `box-sizing` keeps the padding inside the 100%. */
.gaia-wizard-layout__content--fill {
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* Under the flow breakpoint the rail leaves the side and becomes a top strip:
   the row turns into a column and the sider spans it. A GaiaStepFlow slotted
   into #rail flips to its horizontal bar at the same width, so the two moves
   read as one. The footer stays pinned at the bottom either way.

   Keyed to the VIEWPORT, deliberately not to the wizard's own width: sizing
   off the wizard would flip the whole layout every time the app sidebar
   opens or collapses, which reads as the page jumping around. The threshold
   is instead the worst case — the widest step body (sections nav + form +
   live preview, ~1100px) plus the 280px rail plus an open sidebar — so on a
   laptop the strip is simply always on, sidebar or no sidebar. */
@media (max-width: 1799px) {
  .gaia-wizard-layout__row {
    flex-direction: column;
  }

  .gaia-wizard-layout__row > :deep(.n-layout-sider) {
    flex: none;
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    height: auto;
    border-right: none;
    border-bottom: 1px solid v-bind(stripBorder);
  }

  /* The strip is one row, not the rail's stack: title left, steps centred
     beside it, the hint dropped — vertical space is the scarce resource up
     here. The `gaia-wizard-rail` classes are the convention the wizard views
     put on their #rail markup; !important outranks n-flex's inline styles. */
  .gaia-wizard-layout__row > :deep(.n-layout-sider .n-layout-sider-scroll-container) {
    padding: 10px v-bind(stripPadX) !important;
  }

  /* 1fr auto 1fr: the equal side tracks keep the steps on the page's true
     centre — flexing them into the space after the title lands them right of
     it by half the title's width. */
  :deep(.gaia-wizard-rail) {
    display: grid !important;
    grid-template-columns: 1fr auto 1fr;
    align-items: center !important;
  }

  :deep(.gaia-wizard-rail > *:first-child) {
    justify-self: start;
    min-width: 0;
  }

  :deep(.gaia-wizard-rail .gaia-step-flow) {
    grid-column: 2;
    min-width: 0;
  }

  :deep(.gaia-wizard-rail__hint) {
    display: none;
  }
}
</style>
